import { Component } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { AppState } from '@zerops/zerops/app';
import { selectZefDialogState, zefDialogClose } from '@zerops/zef/dialog';
import { ZefReactiveComponent } from '@zerops/zef/core';
import { ProjectEntity, releaseIPv4, requestIPv4 } from '@zerops/zerops/core/project-base';
import { AddonExtendedByProjectInfo, SubscriptionExtendedByProjectInfo } from '@zerops/models/billing';
import { ipV4AddonButtonText, requestIpV4DisabledReason } from '@zerops/models/project';
import { ClientStatusEntity } from '@zerops/zerops/core/client-status-base';
import { openTopUpDialog } from '@zerops/zerops/feature/top-up-dialog';
import { UserEntity } from '@zerops/zerops/core/user-base';
import { combineLatest, of, Subject } from 'rxjs';
import { filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { FEATURE_NAME } from './addon-activation-dialog.constant';
import { AddonActivationDialogTranslations } from './addon-activation-dialog.translations';
import { canEditFinances } from '@zerops/models/client-user';

@Component({
  selector: 'z-addon-activation-dialog',
  templateUrl: './addon-activation-dialog.container.html',
  styleUrls: [ './addon-activation-dialog.container.scss' ]
})
export class AddonActivationDialogContainer extends ZefReactiveComponent {

  // # Event Streams
  onActivate$ = new Subject<void>();
  onRelease$ = new Subject<void>();
  onOpenTopUpDialog$ = new Subject<void>();
  onCloseDialog$ = new Subject<void>();

  // # Data
  // -- sync
  key = FEATURE_NAME;
  loadingKeys = [ requestIPv4.type, releaseIPv4.type ]
  ipV4AddonButtonText = ipV4AddonButtonText;

  // -- async
  open$ = this._store.pipe(
    select(selectZefDialogState(FEATURE_NAME)),
    map((data) => data.state)
  );
  meta$ = this._store.pipe(
    select(selectZefDialogState(FEATURE_NAME)),
    map((data) => data.meta),
    filter((d) => !!d)
  );
  addon$ = this.meta$.pipe(
    map((d) => d.addon as AddonExtendedByProjectInfo)
  );
  subscription$ = this.meta$.pipe(
    map((d) => d.subscription as SubscriptionExtendedByProjectInfo)
  );
  translations$ = this.translate$<AddonActivationDialogTranslations>(FEATURE_NAME);
  activeClientStatus$ = this._clientStatusEntity.activeClientStatus$;
  activeClient$ = this._userEntity.activeClientUser$;
  isContabo$ = this._userEntity.isContabo$;

  requestIpV4DisabledReason$ = combineLatest([
    this.addon$.pipe(
      switchMap((addon) => addon ? this._projectEntity.entityById$(addon.projectId) : of(undefined))
    ),
    this._userEntity.isActiveClientUnverified$
  ]).pipe(
    map(([ project, clientUnverified ]) => project
      ? requestIpV4DisabledReason(project.status, clientUnverified)
      : undefined
    )
  );

  canEditFinances$ = this.activeClient$.pipe(
    filter((d) => !!d),
    map((d) => canEditFinances(d))
  );

  // a reuse of a paid period charges nothing
  canAfford$ = combineLatest({
    addon: this.addon$,
    clientStatus: this.activeClientStatus$
  }).pipe(
    map(({ addon, clientStatus }) => {
      if (!addon) { return undefined; }

      return addon.unitPrice === 0
        || (clientStatus?.credit ?? 0) + (clientStatus?.promoCredit ?? 0) >= addon.unitPrice;
    })
  );

  // # State resolver
  state = this.$connect({
    translations: this.translations$,
    open: this.open$,
    addon: this.addon$,
    subscription: this.subscription$,
    activeClientStatus: this.activeClientStatus$,
    isContabo: this.isContabo$,
    canEditFinances: this.canEditFinances$,
    canAfford: this.canAfford$,
    requestIpV4DisabledReason: this.requestIpV4DisabledReason$
  });

  // # Action Streams
  private _activateAddonAction$ = this.onActivate$.pipe(
    withLatestFrom(this.addon$),
    map(([ _, { projectId } ]) => requestIPv4(projectId))
  );
  private _releaseAction$ = this.onRelease$.pipe(
    withLatestFrom(this.subscription$),
    map(([ _, { projectId } ]) => releaseIPv4(projectId))
  );
  private _openTopUpDialogAction$ = this.onOpenTopUpDialog$.pipe(
    map(() => openTopUpDialog())
  );
  private _closeDialogAction$ = this.onCloseDialog$.pipe(
    map(() => zefDialogClose({ key: FEATURE_NAME }))
  );

  constructor(
    private _store: Store<AppState>,
    private _clientStatusEntity: ClientStatusEntity,
    private _userEntity: UserEntity,
    private _projectEntity: ProjectEntity
  ) {
    super();

    this.$dispatchActions([
      this._activateAddonAction$,
      this._releaseAction$,
      this._openTopUpDialogAction$,
      this._closeDialogAction$
    ]);
  }
}
